/**
 * @fileoverview Composer service for the Teacher Final Assessment Drawer (AR-579).
 *
 * Read (legacy routes, verified against the real DB / live call):
 *   - GET /projects/:id/list_final_assessments
 *       → [{ id, fullName, imageUrl, assessmentStatus, studentAssessmentStatus }]
 *   - GET /projects/:id/:studentId/student_final_assessment
 *       → { student: { …, contentFinalAssessmentScores[], specificSkillFinalAssessmentScores[] } }
 *
 * The list endpoint has no scores, so we fan-out one detail call per student
 * (1+N, in parallel) and pivot every score by `lv4Item.id` into `perStudent`.
 * This mirrors the lesson drawer's accepted 1+N pattern; a one-shot
 * all-students endpoint is a documented backend gap.
 *
 * Save uses the AR-579 arc endpoint through the shared `createScoreUpdater`
 * (same contract as the lesson drawer; only the URL + id field differ).
 */

import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import { api } from 'helpers'
import legacyFinalAssessment from '@legacy/pages/FinalAssessment/components/StudentsAssessments/assessmentService'
import { createScoreUpdater } from '../scoreUpdateService'
import type {
  CurriculumKind,
  FinalAssessmentDrawerData,
  FinalAssessmentItem,
  FinalDrawerStudent,
  FinalScore,
} from './types'

// Raw shapes after the humps interceptor camelizes the legacy snake_case.
interface RawLv4Item {
  id: string
  name: string
  lv1Name: string
  color: string
  originalCodes: string
}

interface RawFinalScore {
  id: string
  lv4Item: RawLv4Item
  masterScore: number
  studentScore: number
  doNotAssess: boolean
  /** ISO timestamp; present once the student submitted their self-assessment. */
  studentDate: string | null
  howToAssess1?: string
  howToAssess2?: string
  howToAssess3?: string
  howToAssess4?: string
  howToAssess5?: string
}

interface RawStudentDetail {
  id: string
  fullName: string
  masterJournalId: string | null
  masterJournal: string | null
  contentFinalAssessmentScores: RawFinalScore[]
  specificSkillFinalAssessmentScores: RawFinalScore[]
}

interface RawListStudent {
  id: string
  fullName: string
  imageUrl: string
}

function adaptRubrics(raw: RawFinalScore): Record<string, string> {
  const rubrics: Record<string, string> = {}
  if (raw.howToAssess1) rubrics['1'] = raw.howToAssess1
  if (raw.howToAssess2) rubrics['2'] = raw.howToAssess2
  if (raw.howToAssess3) rubrics['3'] = raw.howToAssess3
  if (raw.howToAssess4) rubrics['4'] = raw.howToAssess4
  if (raw.howToAssess5) rubrics['5'] = raw.howToAssess5
  return rubrics
}

function adaptScore(raw: RawFinalScore): FinalScore {
  return {
    id: raw.id,
    masterScore: raw.masterScore ?? 0,
    studentScore: raw.studentScore ?? 0,
    doNotAssess: Boolean(raw.doNotAssess),
    studentAssessed: Boolean(raw.studentDate),
  }
}

/**
 * Pivots one `scores` field across all student details into a per-`lv4Item`
 * list, accumulating each student's score under `perStudent[studentId]`.
 * Item order follows first appearance. Items where every student has
 * `doNotAssess` are flagged `enabled: false`.
 */
function pivotItems(
  details: RawStudentDetail[],
  field: 'contentFinalAssessmentScores' | 'specificSkillFinalAssessmentScores',
  kind: CurriculumKind,
): FinalAssessmentItem[] {
  const byLv4Item = new Map<string, FinalAssessmentItem>()

  for (const detail of details) {
    for (const raw of detail[field] ?? []) {
      const lv4 = raw.lv4Item
      if (!lv4?.id) continue
      let item = byLv4Item.get(lv4.id)
      if (!item) {
        item = {
          lv4ItemId: lv4.id,
          curriculumKind: kind,
          name: lv4.name,
          lv1Name: lv4.lv1Name,
          color: lv4.color,
          originalCodes: lv4.originalCodes ?? '',
          rubrics: adaptRubrics(raw),
          enabled: true,
          perStudent: {},
        }
        byLv4Item.set(lv4.id, item)
      }
      item.perStudent[detail.id] = adaptScore(raw)
    }
  }

  for (const item of byLv4Item.values()) {
    const scores = Object.values(item.perStudent)
    item.enabled = scores.length === 0 || !scores.every(s => s.doNotAssess)
  }

  return Array.from(byLv4Item.values())
}

interface FetchOptions {
  projectId: string
  projectName: string
}

/**
 * Loads the full drawer payload: students (from the list endpoint, preserving
 * its order) and the pivoted standards/skills (from the per-student details).
 */
export async function fetchFinalAssessmentDrawerData({
  projectId,
  projectName,
}: FetchOptions): Promise<FinalAssessmentDrawerData> {
  const rawStudents = (await legacyFinalAssessment.getStudents(projectId)) as unknown as RawListStudent[]

  const details = (await Promise.all(
    rawStudents.map(s => legacyFinalAssessment.getStudent(projectId, s.id)),
  )) as unknown as RawStudentDetail[]

  // The teacher note (master journal) only comes in the detail payload, so
  // merge it into the list students by id.
  const detailById = new Map(details.map(d => [d.id, d]))

  const students: FinalDrawerStudent[] = rawStudents.map(s => ({
    id: s.id,
    fullName: s.fullName,
    avatarUrl: s.imageUrl ?? '',
    journalId: detailById.get(s.id)?.masterJournalId ?? null,
    journal: detailById.get(s.id)?.masterJournal ?? '',
  }))

  return {
    project: { id: projectId, name: projectName },
    students,
    standards: pivotItems(details, 'contentFinalAssessmentScores', 'content'),
    skills: pivotItems(details, 'specificSkillFinalAssessmentScores', 'skill'),
  }
}

/** Final-assessment score updater bound to the AR-579 arc endpoint. */
export function finalScoreUpdater(projectId: string) {
  return createScoreUpdater(
    ARC_MOSAICO_ENDPOINTS.projects.updateFinalAssessmentScores(projectId),
    'finalAssessmentScoreId',
  )
}

/**
 * Persists the teacher note (master journal) of a final assessment (AR-753).
 * Mirrors the legacy `updateIndividualJournal` route the teacher already
 * uses in the final-assessment flow: `PATCH /journals/:id`. No-op when the
 * student has no master journal yet.
 */
export async function updateFinalAssessmentJournal(
  journalId: string | null,
  description: string,
): Promise<void> {
  if (!journalId) return
  await api.patch(`/journals/${journalId}`, { description })
}
